import { Polyline, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import { useTheme, Box, Typography, Button } from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";

// Custom icon for ruler points
const rulerIcon = new L.DivIcon({
  className: "ruler-point",
  html: '<div style="background-color: #ff6b6b; border: 2px solid white; border-radius: 50%; width: 12px; height: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// Calcula a distância em metros entre dois pontos de latitude/longitude
function calculateDistance(point1, point2) {
  const R = 6371000;
  const lat1 = (point1.lat * Math.PI) / 180;
  const lat2 = (point2.lat * Math.PI) / 180;
  const deltaLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const deltaLng = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

const formatDistance = (distance) => {
  if (distance < 1000) {
    return `${distance.toFixed(2)} m`;
  } else if (distance < 100000) {
    return `${(distance / 1000).toFixed(2)} km`;
  } else {
    return `${(distance / 1000).toFixed(0)} km`;
  }
};

const RulerComponent = ({ rulerPoints, onClearRuler, removeLast }) => {
  const theme = useTheme();
  if (!rulerPoints || rulerPoints.length === 0) {
    return null;
  }

  const calculateCumulativeDistance = (index) => {
    if (index === 0) return 0;

    let totalDistance = 0;
    for (let i = 1; i <= index; i++) {
      totalDistance += calculateDistance(rulerPoints[i - 1], rulerPoints[i]);
    }
    return totalDistance;
  };

  const calculateSegmentDistance = (index) => {
    if (index === 0) return 0;
    return calculateDistance(rulerPoints[index - 1], rulerPoints[index]);
  };

  return (
    <>
      {rulerPoints.length > 1 && (
        <Polyline
          positions={rulerPoints.map((point) => [point.lat, point.lng])}
          pathOptions={{
            color: theme.palette.error.main,
            weight: 3,
            opacity: 0.8,
            dashArray: "10, 10",
          }}
        />
      )}

      {rulerPoints.map((point, index) => {
        const cumulativeDistance = calculateCumulativeDistance(index);
        const segmentDistance = calculateSegmentDistance(index);

        return (
          <Marker
            key={`ruler-point-${index}`}
            position={[point.lat, point.lng]}
            icon={rulerIcon}
          >
            <Tooltip
              permanent
              direction="top"
              className="custom-ruler-tooltip"
              offset={[0, -10]}
            >
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  textAlign: "center",
                  backgroundColor: `${theme.palette.background.paper}`,
                  color: theme.palette.text.primary,
                  border: "2px solid",
                  borderColor: theme.palette.divider,
                  borderRadius: "3px",
                  padding: "4px 8px",
                }}
              >
                {index === 0 ? (
                  <Typography variant="caption" fontWeight="bold">
                    Início
                  </Typography>
                ) : (
                  <Box>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                    >
                      Total: {formatDistance(cumulativeDistance)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "11px", opacity: 0.8 }}
                      display="block"
                    >
                      Trecho: {formatDistance(segmentDistance)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Tooltip>
          </Marker>
        );
      })}

      {rulerPoints.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1000,
            display: "flex",
            gap: 2,
            borderRadius: "4px",
            padding: "4px 8px",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              removeLast();
            }}
            sx={{
              fontSize: "12px",
              textTransform: "none",
              minWidth: "auto",
              gap: 1,
              px: 1.5,
              py: 1,
              color: theme.palette.warning.dark,
              borderColor: theme.palette.warning.dark,
              backgroundColor: theme.palette.background.paper,
              transition: "background 0.2s, color 0.2s, border-color 0.2s",
              "&:hover": {
                color: theme.palette.warning.main,
                borderColor: theme.palette.warning.main,
              },
            }}
          >
            <UndoIcon />
            Desfazer
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClearRuler();
            }}
            sx={{
              fontSize: "12px",
              textTransform: "none",
              minWidth: "auto",
              px: 1.5,
              py: 1,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            Sair da régua
          </Button>
        </Box>
      )}
    </>
  );
};

export default RulerComponent;
